import React, { useState, useEffect, useCallback } from "react"
import PropTypes from "prop-types"
import { Spinner } from "reactstrap"
import { getDatafonoConciliacion, descargarDatafonoConciliacionExport } from "../api"
import { formatCurrency, formatNumber, formatFechaCorta, estadoConciliacionConfig, rangoDelMes } from "../helpers"
import CopiarCufe from "../CopiarCufe"

const PAGE_SIZE = 50

const lista = v => {
  if (v == null || v === "") return "—"
  if (Array.isArray(v)) return v.length ? v.join("; ") : "—"
  return String(v)
}

const EstadoBadge = ({ estado }) => {
  const cfg = estadoConciliacionConfig(estado)
  return (
    <span title={cfg.desc} style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 10px", borderRadius: 999, background: cfg.bg, color: cfg.color, fontSize: 11, fontWeight: 700 }}>
      <i className={`mdi mdi-${cfg.icon}`} />{cfg.label}
    </span>
  )
}
EstadoBadge.propTypes = { estado: PropTypes.string }

const ResumenCard = ({ label, value, accent, sub, onClick, activo }) => (
  <div
    onClick={onClick}
    style={{
      flex: "1 1 170px", minWidth: 0, background: "#fff", borderRadius: 12, padding: "14px 16px",
      border: `1px solid ${activo ? accent : "#e2e8f0"}`,
      borderLeft: `4px solid ${accent}`,
      cursor: onClick ? "pointer" : "default",
      boxShadow: activo ? `0 0 0 2px ${accent}22` : "none",
      transition: "box-shadow .15s, border-color .15s",
    }}
  >
    <div style={{ fontSize: 11, color: "#64748b", textTransform: "uppercase", fontWeight: 700, letterSpacing: 0.4 }}>{label}</div>
    <div style={{ fontSize: 21, fontWeight: 700, color: "#0f172a", marginTop: 4 }}>{value}</div>
    {sub && <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 2 }}>{sub}</div>}
  </div>
)
ResumenCard.propTypes = { label: PropTypes.string, value: PropTypes.node, accent: PropTypes.string, sub: PropTypes.node, onClick: PropTypes.func, activo: PropTypes.bool }

const ConciliacionTab = ({ filtros }) => {
  const [estado, setEstado] = useState("")
  const [page, setPage] = useState(1)
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const [expandidas, setExpandidas] = useState(new Set())
  const [exportando, setExportando] = useState(null)

  const rango = () => {
    const fecha_inicio = filtros?.fecha_inicio || rangoDelMes(filtros?.year, filtros?.month).fecha_inicio
    const fecha_fin = filtros?.fecha_fin || rangoDelMes(filtros?.year, filtros?.month).fecha_fin
    return { fecha_inicio, fecha_fin }
  }

  const cargar = useCallback(async (pageIdx, estadoFiltro) => {
    if (!filtros?.nitemisor) return
    setLoading(true)
    setError("")
    try {
      const fecha_inicio = filtros.fecha_inicio || rangoDelMes(filtros.year, filtros.month).fecha_inicio
      const fecha_fin = filtros.fecha_fin || rangoDelMes(filtros.year, filtros.month).fecha_fin
      const { data: response } = await getDatafonoConciliacion({
        nitemisor: filtros.nitemisor,
        fecha_inicio, fecha_fin,
        sucursal: filtros.sucursal,
        estado: estadoFiltro || undefined,
        page: pageIdx,
        page_size: PAGE_SIZE,
      })
      if (response?.success === false) throw new Error(response.error || "El servidor reportó un error.")
      setData(response)
    } catch (err) {
      setError(err.response?.data?.error || err.message || "No se pudo cargar la conciliación.")
      setData(null)
    } finally {
      setLoading(false)
    }
  }, [filtros])

  // Reset al cambiar filtros
  useEffect(() => {
    setPage(1)
    setEstado("")
    setExpandidas(new Set())
    cargar(1, "")
  }, [cargar])

  const filtrarEstado = nuevoEstado => {
    const e = estado === nuevoEstado ? "" : nuevoEstado
    setEstado(e)
    setPage(1)
    setExpandidas(new Set())
    cargar(1, e)
  }

  const irPagina = nueva => {
    const totalPages = data?.pagination?.total_pages || 1
    if (nueva < 1 || nueva > totalPages) return
    setPage(nueva)
    setExpandidas(new Set())
    cargar(nueva, estado)
  }

  const handleExport = async formato => {
    if (!filtros?.nitemisor || exportando) return
    setExportando(formato)
    setError("")
    try {
      const { fecha_inicio, fecha_fin } = rango()
      await descargarDatafonoConciliacionExport({
        nitemisor: filtros.nitemisor,
        fecha_inicio, fecha_fin,
        sucursal: filtros.sucursal,
        estado: estado || undefined,
        formato,
      })
    } catch (err) {
      setError(err.message || "Error al exportar.")
    } finally {
      setExportando(null)
    }
  }

  const toggle = cufe => setExpandidas(prev => {
    const next = new Set(prev)
    if (next.has(cufe)) next.delete(cufe); else next.add(cufe)
    return next
  })

  const resumen = data?.resumen || {}
  const filas = data?.data || []
  const pagi = data?.pagination || {}
  const sinDatafono = data && Number(resumen.total_cufes) === 0 && !estado

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Barra export */}
      <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 14, padding: "12px 16px", display: "flex", flexWrap: "wrap", alignItems: "center", gap: 12 }}>
        <div style={{ fontSize: 13, color: "#475569" }}>
          <i className="mdi mdi-scale-balance me-1 text-primary" />
          Datáfono (confirmado) <strong>vs</strong> POS (reportado), por factura
        </div>
        <div style={{ marginLeft: "auto" }} className="btn-group btn-group-sm">
          <button type="button" className="btn btn-outline-success" onClick={() => handleExport("xlsx")} disabled={!!exportando || sinDatafono || !data}>
            {exportando === "xlsx" ? <><Spinner size="sm" className="me-1" /> …</> : <><i className="mdi mdi-file-excel me-1" /> Excel</>}
          </button>
          <button type="button" className="btn btn-outline-primary" onClick={() => handleExport("csv")} disabled={!!exportando || sinDatafono || !data}>
            {exportando === "csv" ? <><Spinner size="sm" className="me-1" /> …</> : <><i className="mdi mdi-file-delimited me-1" /> CSV</>}
          </button>
        </div>
      </div>

      {error && (
        <div style={{ padding: "14px 18px", background: "#fef2f2", border: "1px solid #fecaca", color: "#991b1b", borderRadius: 10, display: "flex", alignItems: "center", gap: 10 }}>
          <i className="mdi mdi-alert-circle" style={{ fontSize: 20 }} />
          <span>{error}</span>
          <button type="button" className="btn btn-sm btn-outline-danger ms-auto" onClick={() => cargar(page, estado)}>Reintentar</button>
        </div>
      )}

      {loading && !data && (
        <div style={{ background: "#fff", borderRadius: 14, padding: 80, textAlign: "center", color: "#64748b", border: "1px solid #e2e8f0" }}>
          <Spinner color="primary" />
          <div className="mt-2">Cargando conciliación…</div>
        </div>
      )}

      {sinDatafono && (
        <div style={{ background: "linear-gradient(135deg, #f8fafc, #ffffff)", border: "1px dashed #cbd5e1", borderRadius: 14, padding: "50px 40px", textAlign: "center" }}>
          <i className="mdi mdi-credit-card-off-outline" style={{ fontSize: 52, color: "#cbd5e1" }} />
          <h5 className="mt-3" style={{ color: "#0f172a", fontWeight: 600 }}>No hay datáfono para conciliar en el período</h5>
          <p style={{ color: "#64748b", maxWidth: 480, margin: "8px auto 0", fontSize: 14 }}>
            La conciliación cruza el <strong>datáfono</strong> (servicio adicional) contra los métodos de pago del POS.
            Si este emisor no tiene datáfono, esta vista no aplicará; el resto del módulo funciona igual para todos.
          </p>
        </div>
      )}

      {data && !sinDatafono && (
        <>
          {/* Tarjetas resumen (clickeables = filtro por estado) */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
            <ResumenCard label="Total facturas" value={formatNumber(resumen.total_cufes)} accent="#3b82f6"
              sub={estado ? "Quita filtro" : "Con datáfono"} onClick={() => filtrarEstado("")} activo={!estado} />
            <ResumenCard label="Cuadran" value={formatNumber(resumen.cuadran)} accent="#10b981"
              sub="Datáfono = tarjeta POS" onClick={() => filtrarEstado("CUADRA")} activo={estado === "CUADRA"} />
            <ResumenCard label="Con diferencia" value={formatNumber(resumen.con_diferencia)} accent="#ef4444"
              sub="Revisar" onClick={() => filtrarEstado("DIFERENCIA")} activo={estado === "DIFERENCIA"} />
            <ResumenCard label="Sin reporte" value={formatNumber(resumen.sin_reporte)} accent="#f59e0b"
              sub="POS sin métodos" onClick={() => filtrarEstado("SIN_REPORTE")} activo={estado === "SIN_REPORTE"} />
          </div>

          {/* Sumas */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
            <div style={sumaBox}><span style={sumaLbl}>Σ Datáfono</span><span style={{ ...sumaVal, color: "#059669" }}>{formatCurrency(resumen.suma_valor_datafono)}</span></div>
            <div style={sumaBox}><span style={sumaLbl}>Σ Tarjeta reportada (POS)</span><span style={{ ...sumaVal, color: "#1e40af" }}>{formatCurrency(resumen.suma_valor_tarjeta_reportado)}</span></div>
            <div style={sumaBox}><span style={sumaLbl}>Σ Diferencia</span><span style={{ ...sumaVal, color: (Number(resumen.suma_diferencia) || 0) === 0 ? "#059669" : "#dc2626" }}>{formatCurrency(resumen.suma_diferencia)}</span></div>
          </div>

          {/* Tabla */}
          <div style={{ background: "#fff", borderRadius: 14, padding: 20, border: "1px solid #e2e8f0" }}>
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
              <h6 style={{ margin: 0, color: "#0f172a", fontWeight: 600 }}>
                <i className="mdi mdi-table-large me-2 text-primary" />
                Facturas {estado && <EstadoBadge estado={estado} />}
                <span style={{ fontWeight: 400, color: "#64748b", marginLeft: 8, fontSize: 13 }}>({formatNumber(pagi.total_records)} registros)</span>
              </h6>
              {loading && <Spinner size="sm" color="primary" />}
            </div>

            <div style={{ border: "1px solid #e2e8f0", borderRadius: 8, overflow: "hidden" }}>
              <div style={{ maxHeight: 580, overflowY: "auto", overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 900 }}>
                  <thead style={{ position: "sticky", top: 0, background: "#f1f5f9", zIndex: 1 }}>
                    <tr>
                      <th style={{ padding: "10px 12px", width: 40 }} />
                      <th style={th}>Folio</th>
                      <th style={th}>Fecha</th>
                      <th style={th}>Sucursal</th>
                      <th style={{ ...th, textAlign: "right" }}>Total factura</th>
                      <th style={{ ...th, textAlign: "right" }}>Datáfono</th>
                      <th style={{ ...th, textAlign: "right" }}>Tarjeta POS</th>
                      <th style={{ ...th, textAlign: "right" }}>Diferencia</th>
                      <th style={{ ...th, textAlign: "center" }}>Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filas.length === 0 && (
                      <tr><td colSpan={9} style={{ padding: 40, textAlign: "center", color: "#64748b" }}>Sin registros para el filtro aplicado.</td></tr>
                    )}
                    {filas.map(f => {
                      const exp = expandidas.has(f.cufe)
                      const dif = Number(f.diferencia) || 0
                      return (
                        <React.Fragment key={f.cufe}>
                          <tr style={{ borderBottom: "1px solid #f1f5f9", background: exp ? "#eff6ff" : "#fff", cursor: "pointer" }} onClick={() => toggle(f.cufe)}>
                            <td style={{ padding: "9px 12px", textAlign: "center", color: "#64748b" }}>
                              <i className={`mdi mdi-chevron-${exp ? "down" : "right"}`} style={{ fontSize: 18 }} />
                            </td>
                            <td style={{ padding: "9px 12px", fontFamily: "ui-monospace, monospace", fontSize: 12, color: "#0f172a" }}>{f.prefijo}-{f.folio}</td>
                            <td style={{ padding: "9px 12px", color: "#475569", fontSize: 12 }}>{formatFechaCorta(f.fecha)}</td>
                            <td style={{ padding: "9px 12px", color: "#475569" }}><i className="mdi mdi-store text-primary me-1" />{f.sucursal || "—"}</td>
                            <td style={{ padding: "9px 12px", textAlign: "right", color: "#0f172a", fontWeight: 600 }}>{formatCurrency(f.total_factura)}</td>
                            <td style={{ padding: "9px 12px", textAlign: "right", color: "#059669", fontWeight: 700 }}>{formatCurrency(f.valor_datafono)}</td>
                            <td style={{ padding: "9px 12px", textAlign: "right", color: "#1e40af" }}>{formatCurrency(f.valor_tarjeta_reportado)}</td>
                            <td style={{ padding: "9px 12px", textAlign: "right", color: dif === 0 ? "#94a3b8" : "#dc2626", fontWeight: dif === 0 ? 400 : 700 }}>
                              {dif !== 0 ? formatCurrency(dif) : "—"}
                            </td>
                            <td style={{ padding: "9px 12px", textAlign: "center" }}><EstadoBadge estado={f.estado} /></td>
                          </tr>
                          {exp && (
                            <tr style={{ background: "#fafafa" }}>
                              <td />
                              <td colSpan={8} style={{ padding: "12px 16px" }}>
                                <div style={{ display: "flex", flexWrap: "wrap", gap: 24 }}>
                                  <div style={{ flex: "1 1 300px", minWidth: 0 }}>
                                    <div style={detTit}><i className="mdi mdi-credit-card-check-outline me-1" style={{ color: "#059669" }} />Datáfono (confirmado)</div>
                                    <Det k="Transacciones" v={formatNumber(f.n_transacciones_datafono)} />
                                    <Det k="Valor" v={formatCurrency(f.valor_datafono)} />
                                    <Det k="Medios" v={lista(f.medios_datafono)} />
                                    <Det k="Aprobaciones" v={lista(f.aprobaciones_datafono)} />
                                    <Det k="Horas" v={lista(f.horas_datafono)} />
                                    <Det k="Fechas de pago" v={lista(f.fechas_pago_datafono)} />
                                  </div>
                                  <div style={{ flex: "1 1 300px", minWidth: 0 }}>
                                    <div style={detTit}><i className="mdi mdi-cash-register me-1" style={{ color: "#1e40af" }} />POS (reportado)</div>
                                    <Det k="# métodos" v={formatNumber(f.n_metodos_reportados)} />
                                    <Det k="Métodos" v={lista(f.metodos_reportados)} />
                                    <Det k="Valor reportado total" v={formatCurrency(f.valor_reportado_total)} />
                                    <Det k="Valor tarjeta reportado" v={formatCurrency(f.valor_tarjeta_reportado)} />
                                    <Det k="Dif. vs total (no-tarjeta)" v={formatCurrency(f.diferencia_vs_total)} />
                                  </div>
                                  <div style={{ flex: "1 1 260px", minWidth: 0 }}>
                                    <div style={detTit}><i className="mdi mdi-account me-1" style={{ color: "#8b5cf6" }} />Receptor</div>
                                    <Det k="Nombre" v={f.nombrereceptor || "—"} />
                                    <Det k="NIT" v={f.nitreceptor || "—"} />
                                    <Det k="Correo" v={f.mail_receptor || "—"} />
                                    <div style={{ marginTop: 6 }}><CopiarCufe cufe={f.cufe} /></div>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {(pagi.total_pages || 1) > 1 && (
              <div className="d-flex justify-content-between align-items-center mt-3">
                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => irPagina(page - 1)} disabled={!pagi.has_prev || loading}>
                  <i className="mdi mdi-chevron-left" /> Anterior
                </button>
                <span style={{ fontSize: 13, color: "#64748b" }}>
                  {loading && <Spinner size="sm" className="me-2" />}
                  Página {pagi.page || page} de {pagi.total_pages}
                </span>
                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => irPagina(page + 1)} disabled={!pagi.has_next || loading}>
                  Siguiente <i className="mdi mdi-chevron-right" />
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )
}

const Det = ({ k, v }) => (
  <div style={{ display: "flex", gap: 8, fontSize: 12, padding: "2px 0" }}>
    <span style={{ color: "#94a3b8", minWidth: 150 }}>{k}:</span>
    <span style={{ color: "#0f172a", wordBreak: "break-word" }}>{v}</span>
  </div>
)
Det.propTypes = { k: PropTypes.string, v: PropTypes.node }

ConciliacionTab.propTypes = { filtros: PropTypes.object }

const th = { padding: "10px 12px", textAlign: "left", fontSize: 11, color: "#475569", textTransform: "uppercase", fontWeight: 600, whiteSpace: "nowrap" }
const sumaBox = { flex: "1 1 220px", minWidth: 0, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: "12px 16px", display: "flex", flexDirection: "column", gap: 2 }
const sumaLbl = { fontSize: 11, color: "#64748b", textTransform: "uppercase", fontWeight: 600, letterSpacing: 0.4 }
const sumaVal = { fontSize: 18, fontWeight: 700 }
const detTit = { fontSize: 12, fontWeight: 700, color: "#0f172a", marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.3 }

export default ConciliacionTab
